import './export.dependencies.loader';
import Vue from 'vue';

// Vendor imports
import Buefy from 'buefy';
import Vue2Filters from 'vue2-filters';
import { InlineSvgPlugin } from 'vue-inline-svg';
import VueXHelper from '@/modules/core/app/helpers/VueXHelper';
import VueDOMPurifyHTML from 'vue-dompurify-html';

// Project imports
import DevTools from '@/modules/core/devtools/DevTools';
import Logger from '@/modules/core/app/helpers/Logger';
import { i18n } from '@/i18n';
import { commits, dispatches, getters } from '@/modules/core/app/helpers/store';
import '@/modules/core';
import '@/bootstrap';
import App from '@/App';
import { addStoreModules, createStore } from '@/store';
import router from '@/router';
import { AppModuleGroup } from '@/modules/core/app/constants/app.constants';
import { UserThemeType } from '@/modules/ta/user/user.constants';

// Styles
import 'font-awesome/css/font-awesome.css';
import '@/assets/fonts/tapicons/scss/nucleo.scss';
import '@/assets/fonts/dashboard/dashboard.fonts.css';
import '@/assets/fonts/serviceicons/css/style.css';
import '@/assets/fonts/alphabet/css/style.css';
import 'shepherd.js/dist/css/shepherd.css';

import '@/styles/_all.scss';

// Validation rules
import '@/modules/core/form/validations';

// Custom Plugins
import { StaticOptionPlugin } from '@/modules/core/app/plugins/StaticOptionPlugin';
import { LocalOptionPlugin } from '@/modules/core/app/plugins/LocalOptionPlugin';
import { VueI18nCustomPlugin } from '@/modules/core/app/plugins/VueI18nCustomPlugin';
import SegmentService from '@/modules/core/app/services/SegmentService';

// amCharts5 global configuration
import '@/modules/core/charts/am5/charts.globals';
import UuidService from '@/modules/core/app/services/UuidService';

if (window.useAdobePptConverter) {
  import(
    /* webpackChunkName: "adobe-dashboard-fonts" */ '@/assets/fonts/dashboard/adobe.dashboard.fonts.css'
  );
}

const store = createStore();
VueXHelper.setStore(store);

// Global Config
Vue.config.productionTip = false;
// Vue lifecycle performance marks (performance.mark/measure) accumulate in the browser's
// Performance Timeline and are never evicted — in production sessions with many section
// switches this can grow to 50+ MB.
// eslint-disable-next-line tap/no-raw-text-js
Vue.config.performance = process.env.NODE_ENV === 'development';

// Plugins
Vue.use(Buefy, {
  // eslint-disable-next-line tap/no-raw-text-js
  defaultIconPack: 'fa',
});
Vue.use(StaticOptionPlugin);
Vue.use(LocalOptionPlugin);
Vue.use(VueI18nCustomPlugin);
Vue.use(Vue2Filters);
/* eslint-disable tap/no-raw-text-js */
Vue.use(VueDOMPurifyHTML, {
  namedConfigurations: {
    linkBlank: {
      // allow these attributes in sanitized HTML
      // eslint-disable-next-line tap/no-raw-text-js
      ADD_ATTR: ['target', 'rel'],
      ALLOWED_ATTR: ['href', 'target', 'rel'],
      ADD_URI_SAFE_ATTR: ['target'],
      KEEP_CONTENT: true,
    },
    /* eslint-disable tap/no-raw-text-js */
    // Chat / LLM response rendering: allows formatting + inline styles
    // but no anchors/images/tables. DOMPurify still blocks dangerous CSS
    // values (javascript: URLs, expression(), etc.) inside the style attr.
    chatBasic: {
      ALLOWED_TAGS: [
        'p',
        'br',
        'strong',
        'b',
        'em',
        'i',
        'u',
        'ul',
        'ol',
        'li',
        'code',
        'pre',
        'span',
        'div',
        'h1',
        'h2',
        'h3',
        'h4',
        'h5',
        'h6',
        'blockquote',
        'hr',
        'sub',
        'sup',
        'del',
        'ins',
        'mark',
      ],
      ALLOWED_ATTR: ['class', 'style'],
      ALLOW_DATA_ATTR: true,
    },
    // Same as chatBasic plus tables and anchors with target/rel
    // (for chat surfaces that need linked references).
    chatWithLinks: {
      ALLOWED_TAGS: [
        'p',
        'br',
        'strong',
        'b',
        'em',
        'i',
        'u',
        'ul',
        'ol',
        'li',
        'code',
        'pre',
        'span',
        'div',
        'h1',
        'h2',
        'h3',
        'h4',
        'h5',
        'h6',
        'blockquote',
        'hr',
        'table',
        'thead',
        'tbody',
        'tr',
        'th',
        'td',
        'a',
      ],
      ALLOWED_ATTR: ['class', 'style', 'href', 'target', 'rel'],
      ADD_URI_SAFE_ATTR: ['target'],
    },
    // Bare <tr>/<td> fragments lose their tags when DOMPurify's default
    // HTML parser fixes up table context. XHTML parsing preserves them.
    tableRows: {
      ALLOWED_TAGS: ['tr', 'td', 'th'],
      ALLOWED_ATTR: [],
      PARSER_MEDIA_TYPE: 'application/xhtml+xml',
    },
    /* eslint-enable tap/no-raw-text-js */
  },
});
/* eslint-enable tap/no-raw-text-js */
Vue.use(InlineSvgPlugin);

if (!window.isExport) {
  window.__tap__ = {
    get segmentvars() {
      return SegmentService.getSegmentVars();
    },
  };
}

// @todo: @dannyyassine move after login
if (DevTools.isEnabled()) {
  window.__vuex__ = {
    commits,
    dispatches,
    getters,
    store,
  };
}
window.Logger = Logger;

// eslint-disable-next-line tap/no-raw-text-js
const APP_ID = '#app';

if (window.isExport) {
  window.exportReady = false;
  (async () => {
    // main store only includes core/<module>.  widget is in ta/widget
    await addStoreModules(VueXHelper.getStore(), AppModuleGroup.TA);
    dispatches.export.setConfig(window.exportVars);
    // force light mode for print
    window.userSettings.themeType = UserThemeType.TYPE_LIGHT;
    dispatches.session.updateUserSettings(window.userSettings);
    const exportComponent = await import(
      /* webpackChunkName: "export-page" */ '@/modules/core/export/components/ExportApp.vue'
    );
    new Vue({
      store,
      i18n,
      render: (h) => h(exportComponent.default),
    }).$mount(APP_ID);
  })();
} else {
  window.addEventListener('beforeunload', () => {
    UuidService.kill();
  });

  new Vue({
    router,
    store,
    i18n,
    render: (h) => h(App),
  }).$mount(APP_ID);
}
